import { Field, Form, Formik } from "formik";
import _isEqual from "lodash/isEqual";
import { useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom/cjs/react-router-dom.min";
import * as Yup from "yup";
import { format } from "date-fns";

import Button from "components/lib/Shared/Button.jsx";
import DatePickerField from "components/lib/Shared/DatePickerField";
import ReactSelectWithFormik from "components/lib/Shared/DropdownSelect";
import Input from "components/lib/Shared/Input";
import ValidationError from "components/lib/Shared/ValidationError";
import addressOptions from "components/PLCTABS/SFLdata/addressOptions";
import countyOptions from "components/PLCTABS/SFLdata/countyOptions";
import { Constants } from "config/constants";
import { Roles_Permissions_Constants } from "config/roles-permissions";
import ManualTransactionEditContext from "context/ManualTransactionEditContext";
import UpdateCompanyDetailsContext from "context/UpdateCompanyDetails";
import { useToast } from "hooks/useToast";
import { handleCompanyMaintenanceFormData } from "utils/transactionRequestHandler";

const RegisteredAddress = ({ handleGoBack, handleGoForward }) => {
  const dispatch = useDispatch();
  const { company, dashboardRoles } = useSelector(
    (state) => state.companyDashboardSlice
  );

  const { batchNo } = useSelector((state) => state.batchNumberSlice);

  const { toast } = useToast(5000);

  const {
    isInUpdateCompanyDetailsContext,
    addUniqueTransactionId,
    transactionExists,
  } = useContext(UpdateCompanyDetailsContext);

  const { transactionToEdit, isInManualTransactionEditContext } = useContext(
    ManualTransactionEditContext
  );

  const { registeredOffice } = company;

  const { companyId } = useParams();

  // Key for localStorage
  const STORAGE_KEY = `registeredAddressForm_${company?._id || "default"}`;

  const [dropdownData, setDropdownData] = useState({
    county: countyOptions.residence.county,
    district: [],
    locality: [],
  });

  const [isLoaded, setIsLoaded] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Load initial values from localStorage or fallback to company data

  const initialValues = {
    postalAddress: registeredOffice?.postalAddress ?? "N/A",
    postalCode: registeredOffice?.postalCode ?? "N/A",
    serviceAddress: registeredOffice?.serviceAddress ?? "N/A",
    companyEmailAddress: registeredOffice?.companyEmailAddress ?? "N/A",
    mobileNumber: registeredOffice?.mobileNumber ?? "N/A",
    buildingName: registeredOffice?.buildingName ?? "N/A",
    houseNumber: registeredOffice?.houseNumber ?? "N/A",
    estate: registeredOffice?.estate ?? "N/A",
    floorNumber: registeredOffice?.floorNumber ?? "N/A",
    streetName: registeredOffice?.streetName ?? "N/A",
    streetNumber: registeredOffice?.streetNumber ?? "N/A",
    location: {
      county: registeredOffice?.location.county ?? "N/A",
      district: registeredOffice?.location.district ?? "N/A",
      locality: registeredOffice?.location.locality ?? "N/A",
    },
    dateOfChange: format(new Date(), "yyyy/MM/dd"),
  };
  const getInitialValues = () => {
    const savedValues = localStorage.getItem(STORAGE_KEY);

    if (transactionToEdit?.companyChangedFields) {
      return {
        ...initialValues,
        ...transactionToEdit.companyChangedFields?.registeredOffice,
      };
    }
    return savedValues ? JSON.parse(savedValues) : initialValues;
  };

  // Validation schema
  const validationSchema = Yup.object().shape({
    dateOfChange: Yup.date().required("Date of change is required"),
  });

  // Get changed fields
  const getChangedFields = (values, initialValues) => {
    const changedFields = {};

    // Compare top-level fields
    Object.keys(values).forEach((key) => {
      if (!_isEqual(values[key], initialValues[key])) {
        changedFields[key] = values[key];
      }
    });

    // Wrap changes in registeredOffice object
    return { registeredOffice: changedFields };
  };

  // Handle form submission
  const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      const initialValues = company?.registeredOffice;
      // Get changed fields
      const changedFields = getChangedFields(values, initialValues);
      if (Object.keys(changedFields.registeredOffice).length === 0) {
        toast("error", "You have not made changes.");
        return; // No changes, exit
      }

      if (
        Object.keys(changedFields.registeredOffice).length === 1 &&
        changedFields.registeredOffice.dateOfChange
      ) {
        toast("error", "You have not made changes.");
        return; // No changes, exit
      }

      // Update localStorage with the latest values
      localStorage.setItem(STORAGE_KEY, JSON.stringify(values));

      let transactionPayload = {};

      const existingTransaction = transactionExists(
        Constants.TRANSACTION_CHANGE_TYPES.CHANGE_OF_REGISTERED_ADDRESS
      );

      // Create transaction
      if (
        (isInManualTransactionEditContext &&
          transactionToEdit?.changeType ===
            Constants.TRANSACTION_CHANGE_TYPES.CHANGE_OF_REGISTERED_ADDRESS) ||
        existingTransaction
      ) {
        transactionPayload = {
          companyId,
          companyChangedFields: {
            ...(changedFields || existingTransaction?.companyChangedFields),
            effectiveFromDate: changedFields?.registeredOffice?.dateOfChange,
          },
          documentId: transactionToEdit?._id || existingTransaction?._id,
          batchNo,
        };
        const result = await handleCompanyMaintenanceFormData(
          "existing-request",
          transactionPayload
        );

        if (result?.error) {
          toast(
            "error",
            "There was an error updating the transaction. Please try again."
          );
          return;
        }

        if (result?.payload?.status === true) {
          toast("success", "Transaction has been updated successfully");
        }

        handleGoForward();
      } else {
        transactionPayload = {
          changeType:
            Constants.TRANSACTION_CHANGE_TYPES.CHANGE_OF_REGISTERED_ADDRESS,
          applicationType:
            Constants.TRANSACTION_APPLICATION_TYPES.CHANGE_REGISTRATION,
          transactionType: Constants.TRANSACTION_TYPES.MANUAL,
          companyId,
          companyChangedFields: {
            ...changedFields,
            effectiveFromDate: changedFields?.registeredOffice?.dateOfChange,
          },
          batchNo,
        };
        const result = await handleCompanyMaintenanceFormData(
          "new-request",
          transactionPayload,
          companyId
        );
        if (result?.payload?.errors) {
          toast(
            "error",
            "There was an error creating the transaction. Please try again."
          );
          return;
        }

        if (result?.payload?.status === true) {
          toast("success", "Transaction has been created successfully");
        }

        addUniqueTransactionId(result?.payload?.savedTransaction?._id);

        handleGoForward();
      }
      setSubmitting(false);
    } catch (error) {
      // Optionally handle error (e.g., show error message)
    } finally {
      setSubmitting(false);
    }
  };

  // Initialize isLoaded
  useEffect(() => {
    setIsLoaded(true);
  }, []);

  // Handle dropdown selections
  const handleSelect = (type) => (option) => {
    if (type === "county") {
      setDropdownData({
        ...dropdownData,
        district: addressOptions.district.filter(
          (item) => item.county === option.value
        ),
        locality: [],
      });
    }
    if (type === "district") {
      setDropdownData({
        ...dropdownData,
        locality: addressOptions.locality.filter(
          (item) => item.district === option.value
        ),
      });
    }
    return option; // Return option for ReactSelectWithFormik
  };

  if (!company?._id) return "loading...";

  const isRegistrar = dashboardRoles?.some(
    (role) =>
      role.name === Roles_Permissions_Constants.DASHBOARD_ROLES.REGISTRAR
  );

  // Fields array for form
  const fields = [
    [
      {
        key: "Postal Address",
        name: "postalAddress",
        type: "text",
        isEditable: true,
      },
      {
        key: "Postal Code",
        name: "postalCode",
        type: "select",
        isEditable: false,
        options: addressOptions.postalCodes,
      },
      {
        key: "Service Address",
        name: "serviceAddress",
        type: "text",
        isEditable: false,
      },
    ],
    [
      {
        key: "Company Email",
        name: "companyEmailAddress",
        type: "email",
        isEditable: false,
      },
      {
        key: "Mobile Number",
        name: "mobileNumber",
        type: "tel",
        isEditable: false, // Example: non-editable field
      },
    ],
    [
      {
        key: "Building Name",
        name: "buildingName",
        type: "text",
        isEditable: true,
      },
      {
        key: "House Number",
        name: "houseNumber",
        type: "text",
        isEditable: true,
      },
      {
        key: "Estate",
        name: "estate",
        type: "text",
        isEditable: true,
      },
      {
        key: "Floor",
        name: "floorNumber",
        type: "text",
        isEditable: false,
      },
      {
        key: "Street Name",
        name: "streetName",
        type: "text",
        isEditable: true,
      },
      {
        key: "Street Number",
        name: "streetNumber",
        type: "text",
        isEditable: false,
      },
    ],
    [
      {
        key: "County",
        name: "location.county",
        type: "select",
        isEditable: false,
        options: countyOptions.residence.county,
      },
      {
        key: "District",
        name: "location.district",
        type: "select",
        isEditable: false,
        options: dropdownData.district,
      },
      {
        key: "Locality",
        name: "location.locality",
        type: "select",
        isEditable: false,
        options: dropdownData.locality,
      },
    ],
  ];

  return (
    <Formik
      enableReinitialize
      initialValues={getInitialValues()}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ values, errors, setFieldValue, touched }) => {
        // Save form values to localStorage whenever they change
        useEffect(() => {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(values));
        }, [values]);

        // Update district options when county changes
        useEffect(() => {
          setDropdownData({
            ...dropdownData,
            district: countyOptions.residence.district.filter(
              (item) => item.county === values.location.county
            ),
            locality: countyOptions.residence.locality.filter(
              (item) => item.district === values.location.district
            ),
          });
        }, [setDropdownData]);

        // Reset district and locality when county changes
        useEffect(() => {
          if (isLoaded) {
            setFieldValue("location", {
              ...values.location,
              district: "",
              locality: "",
            });
          }
        }, [values.location.county, setFieldValue]);

        // Reset locality when district changes
        useEffect(() => {
          if (isLoaded) {
            setFieldValue("location", {
              ...values.location,
              locality: "",
            });
          }
        }, [values.location.district, setFieldValue]);

        const handleDateOfChange = (date) => {
          if (!date) {
            setFieldValue("dateOfChange", "");
            return;
          }
          if (isNaN(date.getTime())) {
            console.error("Invalid date:", date);
            return;
          }

          // Format the valid date
          const formattedDate = format(date, "yyyy/MM/dd");
          setFieldValue("dateOfChange", formattedDate);
        };

        return (
          <Form className="divide-y-2">
            {/* Group 1: Postal Address, Postal Code, Service Address */}
            <div className="px-4 py-6 bg-white rounded-sm shadow-sm sm:rounded md:rounded-md sm:p-6">
              <div className="flex flex-col space-y-2">
                {fields[0].map((field, index) => (
                  <div key={`${field.key}_${index}`} className="flex flex-col">
                    <div className="flex items-baseline justify-between">
                      <label
                        htmlFor={field.name}
                        className="block mb-2 text-sm text-primary font-medium min-w-[13rem] w-52 whitespace-normal"
                      >
                        {field.key}
                      </label>
                      <div
                        className={`bg-formInput block w-full p-2 text-sm rounded-md`}
                      >
                        {field.type === "select" ? (
                          <Field
                            name={field.name}
                            component={ReactSelectWithFormik}
                            options={field.options}
                            errorPath="postalAddress.postalCode"
                            placeholder="Select postal code"
                            onBlur={field.onBlur}
                            onChange={(option) => {
                              setFieldValue(field.name, option.value);
                            }}
                            isDisabled={field.disabled}
                          />
                        ) : (
                          <Field
                            name={field.name}
                            id={field.name}
                            placeholder={`Add ${field.key}`}
                            component={Input}
                            disabled={field.disabled}
                            className="w-full bg-transparent text-primary placeholder-primary focus:outline-none"
                            type={field.type}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Group 2: Company Email, Mobile Number */}
            <div className="px-4 py-6 bg-white rounded-sm shadow-sm sm:rounded md:rounded-md sm:p-6">
              <div className="flex flex-col space-y-2">
                {fields[1].map((field, index) => (
                  <div key={`${field.key}_${index}`} className="flex flex-col">
                    <div className="flex items-baseline justify-between">
                      <label
                        htmlFor={field.name}
                        className="block mb-2 text-sm text-primary font-medium min-w-[13rem] w-52 whitespace-normal"
                      >
                        {field.key}
                      </label>

                      <div
                        className={`bg-formInput block w-full p-2 text-sm rounded-md`}
                      >
                        <Field
                          name={field.name}
                          id={field.name}
                          placeholder={`Add ${field.key}`}
                          component={Input}
                          disabled={field.disabled}
                          className="w-full bg-transparent text-primary placeholder-primary focus:outline-none"
                          type={field.type}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Group 3: Building Name, House Number, Estate, Floor, Street Name, Street Number */}
            <div className="px-4 py-6 bg-white rounded-sm shadow-sm sm:rounded md:rounded-md sm:p-6">
              <div className="flex flex-col space-y-2">
                {fields[2].map((field, index) => (
                  <div key={`${field.key}_${index}`} className="flex flex-col">
                    <div className="flex items-baseline justify-between">
                      <label
                        htmlFor={field.name}
                        className="block mb-2 text-sm text-primary font-medium min-w-[13rem] w-52 whitespace-normal"
                      >
                        {field.key}
                      </label>

                      <div
                        className={`bg-formInput block w-full p-2 text-sm rounded-md`}
                      >
                        <Field
                          name={field.name}
                          id={field.name}
                          placeholder={`Add ${field.key}`}
                          component={Input}
                          disabled={field.disabled}
                          className="w-full bg-transparent text-primary placeholder-primary focus:outline-none"
                          type={field.type}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Group 4: Country, State, City */}
            <div className="px-4 py-6 bg-white rounded-sm shadow-sm sm:rounded md:rounded-md sm:p-6">
              <div className="flex flex-col space-y-2">
                {fields[3].map((field, index) => (
                  <div key={`${field.key}_${index}`} className="flex flex-col">
                    <div className="flex items-baseline justify-between">
                      <label
                        htmlFor={field.name}
                        className="block mb-2 text-sm text-primary font-medium min-w-[13rem] w-52 whitespace-normal"
                      >
                        {field.key}
                      </label>

                      <div
                        className={`bg-formInput block w-full p-2 text-sm rounded-md`}
                      >
                        <Field
                          name={field.name}
                          component={ReactSelectWithFormik}
                          options={field.options}
                          errorPath={field.name}
                          placeholder={`Select ${field.key}`}
                          onBlur={field.onBlur}
                          onChange={(option) => {
                            const selectedOption = handleSelect(
                              field.key.toLowerCase()
                            )(option);
                            setFieldValue(field.name, selectedOption.value);
                          }}
                          isDisabled={field.disabled}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="px-4 py-6 bg-white rounded-sm shadow-sm sm:rounded md:rounded-md sm:p-6">
              <DatePickerField
                label="Effective date of change for registered address"
                onChange={handleDateOfChange}
                selected={
                  values.dateOfChange ? new Date(values.dateOfChange) : null
                }
                name="dateOfChange"
                placeholderText="Enter date of change"
                errorPath="dateOfChange"
                disabled={false}
                showRequiredAsterisk
              />
              <ValidationError
                errors={errors}
                touched={touched}
                name="dateOfChange"
              />
            </div>

            {/* Submit Button */}
            <div className="px-4 py-3 bg-white sm:px-6">
              <div className="flex justify-end space-x-2">
                <Button
                  type="submit"
                  isLoading={submitting}
                  disabled={submitting}
                  loadingText="Saving..."
                  className="flex px-2 py-2 text-white button button-hover button-disabled button-outline button-focus bg-sflPrimary"
                >
                  Save and proceed
                </Button>
              </div>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};

export default RegisteredAddress;
